import React from "react";
import { useNavigate } from "react-router-dom";
import {
	Box,
	List,
	ListItem,
	ListItemButton,
	ListItemIcon,
	ListItemText,
	Typography,
	IconButton,
	Tooltip,
	Chip,
} from "@mui/material";
import ExploreIcon from "@mui/icons-material/Explore";
import FolderIcon from "@mui/icons-material/Folder";
import AddIcon from "@mui/icons-material/Add";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import CloseIcon from "@mui/icons-material/Close";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import RouteOutlinedIcon from "@mui/icons-material/RouteOutlined";
import PublicOutlinedIcon from "@mui/icons-material/PublicOutlined";
import PhotoAlbumOutlinedIcon from "@mui/icons-material/PhotoAlbumOutlined";
import logo from "../../assets/logo.png";
import logoCuadrado from "../../assets/logo-cuadrado.png";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

const SIDEBAR_WIDTH = 268;
const SIDEBAR_COLLAPSED_WIDTH = 76;

const ACCENT = "#6366F1";
const ACCENT_DARK = "#4F46E5";

const navItemSx = (active, collapsed) => ({
	borderRadius: "12px",
	minHeight: 42,
	justifyContent: collapsed ? "center" : "flex-start",
	px: collapsed ? 1.25 : 1.5,
	mx: collapsed ? 0.5 : 0,
	position: "relative",
	overflow: "hidden",
	"&.Mui-selected": {
		backgroundColor: "rgba(99, 102, 241, 0.1)",
		"& .MuiListItemIcon-root": { color: ACCENT },
		"& .MuiListItemText-primary": {
			color: "#1e1b4b",
			fontWeight: 600,
		},
		"&:hover": {
			backgroundColor: "rgba(99, 102, 241, 0.14)",
		},
		"&::before": {
			content: '""',
			position: "absolute",
			left: 0,
			top: 8,
			bottom: 8,
			width: 3,
			borderRadius: "0 3px 3px 0",
			backgroundColor: ACCENT,
		},
	},
	"&:hover": {
		backgroundColor: "rgba(15, 23, 42, 0.04)",
	},
	transition: "background-color 0.15s ease, color 0.15s ease",
});

const NavItem = ({ item, active, collapsed, onClick }) => (
	<Tooltip title={collapsed ? item.label : ""} placement="right" arrow>
		<ListItem disablePadding sx={{ mb: 0.25 }}>
			<ListItemButton
				selected={active}
				onClick={onClick}
				sx={navItemSx(active, collapsed)}
			>
				<ListItemIcon
					sx={{
						minWidth: collapsed ? 0 : 36,
						color: active ? ACCENT : "#64748B",
						transition: "color 0.15s ease",
					}}
				>
					{item.icon}
				</ListItemIcon>
				{!collapsed && (
					<>
						<ListItemText
							primary={item.label}
							primaryTypographyProps={{
								fontWeight: active ? 600 : 500,
								fontSize: "0.875rem",
								letterSpacing: "-0.01em",
								color: active ? "#1e1b4b" : "#475569",
							}}
						/>
						{item.badge && (
							<Chip
								label={item.badge}
								size="small"
								sx={{
									height: 18,
									fontSize: "0.625rem",
									fontWeight: 700,
									letterSpacing: "0.02em",
									backgroundColor: active
										? "rgba(99, 102, 241, 0.18)"
										: "rgba(99, 102, 241, 0.1)",
									color: ACCENT_DARK,
									border: "none",
									"& .MuiChip-label": { px: 0.75 },
								}}
							/>
						)}
					</>
				)}
			</ListItemButton>
		</ListItem>
	</Tooltip>
);

// ─── Bloque "Perfil viajero" ───
const TravelerProfileBlock = ({
	user,
	collapsed,
	travelCard,
	countriesCount,
	onSectionChange,
	activeSection,
	isMobile,
	onMobileClose,
}) => {
	if (!user) return null;

	const userName =
		user.displayName || user.name || user.email?.split("@")[0] || "Viajero";
	const displayName =
		userName.length > 18 ? userName.slice(0, 18) + "…" : userName;
	const hasTravelCard = !!travelCard;
	const isActive = activeSection === "travel-card";

	const handleClick = () => {
		onSectionChange("travel-card");
		if (isMobile) onMobileClose?.();
	};

	const getSubtext = () => {
		if (!hasTravelCard) return "Crea tu tarjeta viajera";
		if (countriesCount === 0) return "0 sellos";
		return `${countriesCount} ${countriesCount === 1 ? "sello" : "sellos"}`;
	};

	const getActionLabel = () => {
		if (!hasTravelCard) return "";
		if (countriesCount < 3) return "Añade tu próximo sello";
		return "Ver mi tarjeta";
	};

	if (collapsed) {
		return (
			<Tooltip
				title={
					getActionLabel()
						? `${displayName} · ${getSubtext()} · ${getActionLabel()}`
						: `${displayName} · ${getSubtext()}`
				}
				placement="right"
				arrow
			>
				<Box
					onClick={handleClick}
					sx={{
						mx: "auto",
						mb: 1.5,
						width: 42,
						height: 42,
						borderRadius: "12px",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "pointer",
						transition: "all 0.15s ease",
						backgroundColor: isActive
							? "rgba(99, 102, 241, 0.1)"
							: "rgba(15, 23, 42, 0.04)",
						"&:hover": {
							backgroundColor: "rgba(99, 102, 241, 0.12)",
						},
					}}
				>
					<BadgeOutlinedIcon
						sx={{ fontSize: 20, color: isActive ? ACCENT : "#64748B" }}
					/>
				</Box>
			</Tooltip>
		);
	}

	return (
		<Box
			onClick={handleClick}
			sx={{
				mx: 1.5,
				mb: 1.5,
				px: 1.5,
				py: 1.25,
				borderRadius: "14px",
				cursor: "pointer",
				transition: "all 0.15s ease",
				backgroundColor: isActive
					? "rgba(99, 102, 241, 0.1)"
					: "rgba(15, 23, 42, 0.035)",
				border: isActive
					? "1px solid rgba(99, 102, 241, 0.16)"
					: "1px solid transparent",
				"&:hover": {
					backgroundColor: "rgba(99, 102, 241, 0.1)",
				},
			}}
		>
			<Box sx={{ display: "flex", alignItems: "center", gap: 1.25 }}>
				<Box
					sx={{
						width: 32,
						height: 32,
						borderRadius: "9px",
						backgroundColor: isActive
							? "rgba(99, 102, 241, 0.16)"
							: "#fff",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						flexShrink: 0,
					}}
				>
					<BadgeOutlinedIcon
						sx={{
							fontSize: 18,
							color: isActive ? ACCENT : "#64748B",
						}}
					/>
				</Box>
				<Box sx={{ flex: 1, minWidth: 0 }}>
					<Typography
						sx={{
							fontSize: "0.8125rem",
							fontWeight: 600,
							color: "#1e1b4b",
							lineHeight: 1.25,
							overflow: "hidden",
							textOverflow: "ellipsis",
							whiteSpace: "nowrap",
						}}
					>
						{displayName}
					</Typography>
					<Typography
						sx={{
							fontSize: "0.6875rem",
							fontWeight: 500,
							color: isActive ? ACCENT : "#94A3B8",
							lineHeight: 1.3,
							mt: 0.15,
						}}
					>
						{getSubtext()}
					</Typography>
				</Box>
			</Box>
		</Box>
	);
};

const Sidebar = ({
	user,
	activeSection,
	onSectionChange,
	collapsed = false,
	onToggleCollapse,
	isMobile = false,
	mobileOpen = false,
	onMobileClose,
	travelCard = null,
	countriesCount = 0,
	quietCreate = false,
}) => {
	const navigate = useNavigate();

	const handleItemClick = (itemId) => {
		if (itemId === "settings") {
			navigate("/profile");
			if (isMobile) onMobileClose?.();
			return;
		}
		if (itemId === "rutas") {
			navigate("/rutas");
			if (isMobile) onMobileClose?.();
			return;
		}
		if (itemId === "destinos") {
			navigate("/destinos");
			if (isMobile) onMobileClose?.();
			return;
		}
		if (itemId === "album") {
			navigate("/album");
			if (isMobile) onMobileClose?.();
			return;
		}
		onSectionChange(itemId);
		if (isMobile) onMobileClose?.();
	};

	const iconSx = { fontSize: 21 };

	const menuGroups = [
		{
			id: "explore",
			label: "Explorar",
			items: [
				{
					id: "home",
					label: "Inicio",
					icon: <HomeOutlinedIcon sx={iconSx} />,
				},
				{
					id: "explore",
					label: "Guías",
					icon: <ExploreIcon sx={iconSx} />,
				},
				{
					id: "destinos",
					label: "Destinos",
					icon: <PublicOutlinedIcon sx={iconSx} />,
				},
				{
					id: "my-guides",
					label: "Mis guías",
					icon: <FolderIcon sx={iconSx} />,
					requiresAuth: true,
				},
				{
					id: "favorites",
					label: "Favoritos",
					icon: <StarOutlineIcon sx={iconSx} />,
					requiresAuth: true,
				},
			],
		},
		{
			id: "travel",
			label: "Tu viaje",
			items: [
				{
					id: "passport",
					label: "Mi Pasaporte",
					icon: <AutoStoriesOutlinedIcon sx={iconSx} />,
				},
				{
					id: "rutas",
					label: "Rutas",
					icon: <RouteOutlinedIcon sx={iconSx} />,
					badge: "Nuevo",
				},
				{
					id: "album",
					label: "Álbum de viaje",
					icon: <PhotoAlbumOutlinedIcon sx={iconSx} />,
					badge: "Nuevo",
				},
				{
					id: "travel-card",
					label: "Tarjeta viajera",
					icon: <BadgeOutlinedIcon sx={iconSx} />,
				},
			],
		},
		{
			id: "more",
			label: "Más",
			items: [
				{
					id: "modules",
					label: "Módulos",
					icon: <ViewModuleIcon sx={iconSx} />,
					requiresAuth: true,
				},
				{
					id: "discounts",
					label: "Descuentos",
					icon: <LocalOfferOutlinedIcon sx={iconSx} />,
					requiresAuth: true,
				},
			],
		},
	];

	const bottomItems = [
		{
			id: "help",
			label: "Ayuda",
			icon: <HelpOutlineIcon sx={iconSx} />,
		},
		{
			id: "settings",
			label: "Ajustes",
			icon: <SettingsOutlinedIcon sx={iconSx} />,
			requiresAuth: true,
		},
	];

	const visibleGroups = menuGroups
		.map((group) => ({
			...group,
			items: group.items.filter((item) => !item.requiresAuth || user),
		}))
		.filter((group) => group.items.length > 0);

	const filteredBottomItems = bottomItems.filter(
		(item) => !item.requiresAuth || user,
	);

	const sidebarWidth = collapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH;

	const sidebarContent = (
		<Box
			sx={{
				width: isMobile ? SIDEBAR_WIDTH : sidebarWidth,
				height: "100%",
				display: "flex",
				flexDirection: "column",
				backgroundColor: "#fff",
				borderRight: "1px solid rgba(15, 23, 42, 0.06)",
				transition: "width 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
				position: "relative",
			}}
		>
			{/* Brand */}
			<Box
				sx={{
					px: collapsed ? 1.5 : 2,
					pt: 2.25,
					pb: 1.75,
					display: "flex",
					alignItems: "center",
					justifyContent: collapsed ? "center" : "space-between",
					minHeight: 68,
				}}
			>
				{!collapsed && (
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							gap: 1.25,
							cursor: "pointer",
							minWidth: 0,
							"&:hover": { opacity: 0.82 },
						}}
						onClick={() => onSectionChange("home")}
					>
						<Box
							component="img"
							src={logo}
							alt="Viajes Molones"
							sx={{
								height: 34,
								width: "auto",
								objectFit: "contain",
								flexShrink: 0,
							}}
						/>
						<Typography
							sx={{
								fontWeight: 700,
								color: "#0f172a",
								fontSize: "0.98rem",
								letterSpacing: "-0.03em",
								lineHeight: 1.15,
							}}
						>
							Viajes Molones
						</Typography>
					</Box>
				)}

				{collapsed && (
					<Box
						component="img"
						src={logoCuadrado}
						alt="Viajes Molones"
						onClick={() => onSectionChange("home")}
						sx={{
							width: 40,
							height: 40,
							objectFit: "contain",
							cursor: "pointer",
							"&:hover": { opacity: 0.85 },
						}}
					/>
				)}

				{isMobile && (
					<IconButton
						size="small"
						onClick={onMobileClose}
						sx={{
							color: "#64748b",
							backgroundColor: "rgba(15, 23, 42, 0.04)",
							"&:hover": { backgroundColor: "rgba(15, 23, 42, 0.08)" },
						}}
					>
						<CloseIcon fontSize="small" />
					</IconButton>
				)}
			</Box>

			{/* Create */}
			<Box data-nosnippet sx={{ px: collapsed ? 1.25 : 1.75, mb: 2 }}>
				<Tooltip title={collapsed ? "Crear guía" : ""} placement="right" arrow>
					<Box
						onClick={() => onSectionChange("create")}
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: collapsed ? "center" : "flex-start",
							gap: 1.15,
							py: 1.15,
							px: collapsed ? 1.15 : 1.5,
							borderRadius: "12px",
							background: quietCreate
								? "rgba(15, 23, 42, 0.04)"
								: `linear-gradient(135deg, ${ACCENT} 0%, #7C3AED 100%)`,
							color: quietCreate ? "#334155" : "#fff",
							border: quietCreate ? "1px solid #e2e8f0" : "none",
							cursor: "pointer",
							boxShadow: quietCreate
								? "none"
								: "0 8px 18px -10px rgba(99, 102, 241, 0.7)",
							transition: "transform 0.15s ease, box-shadow 0.15s ease, filter 0.15s ease",
							"&:hover": {
								transform: "translateY(-1px)",
								filter: quietCreate ? "none" : "brightness(1.05)",
								background: quietCreate
									? "rgba(15, 23, 42, 0.06)"
									: `linear-gradient(135deg, ${ACCENT} 0%, #7C3AED 100%)`,
							},
							"&:active": { transform: "translateY(0)" },
						}}
					>
						<Box
							sx={{
								width: 22,
								height: 22,
								borderRadius: "7px",
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								backgroundColor: quietCreate
									? "rgba(15, 23, 42, 0.06)"
									: "rgba(255,255,255,0.18)",
								flexShrink: 0,
							}}
						>
							<AddIcon sx={{ fontSize: 16 }} />
						</Box>
						{!collapsed && (
							<Typography
								sx={{
									fontWeight: 600,
									fontSize: "0.875rem",
									letterSpacing: "-0.01em",
								}}
							>
								Crear nueva guía
							</Typography>
						)}
					</Box>
				</Tooltip>
			</Box>

			{user && (
				<TravelerProfileBlock
					user={user}
					collapsed={collapsed}
					travelCard={travelCard}
					countriesCount={countriesCount}
					onSectionChange={onSectionChange}
					activeSection={activeSection}
					isMobile={isMobile}
					onMobileClose={onMobileClose}
				/>
			)}

			{/* Navigation */}
			<Box
				sx={{
					px: collapsed ? 0.75 : 1.25,
					flex: 1,
					overflowY: "auto",
					pb: 1,
					"&::-webkit-scrollbar": { width: 4 },
					"&::-webkit-scrollbar-thumb": {
						backgroundColor: "rgba(15, 23, 42, 0.12)",
						borderRadius: 4,
					},
				}}
			>
				{visibleGroups.map((group, index) => (
					<Box key={group.id} sx={{ mb: index === visibleGroups.length - 1 ? 0 : 1.25 }}>
						<Typography
							sx={{
								px: collapsed ? 0 : 1.25,
								pt: index === 0 ? 0.25 : 1,
								pb: 0.6,
								fontSize: "0.625rem",
								fontWeight: 700,
								color: "#94A3B8",
								textTransform: "uppercase",
								letterSpacing: "0.1em",
								display: collapsed ? "none" : "block",
							}}
						>
							{group.label}
						</Typography>
						{collapsed && index > 0 && (
							<Box
								sx={{
									height: 1,
									mx: 1.5,
									my: 1,
									backgroundColor: "rgba(15, 23, 42, 0.06)",
								}}
							/>
						)}
						<List sx={{ py: 0 }}>
							{group.items.map((item) => (
								<NavItem
									key={item.id}
									item={item}
									active={activeSection === item.id}
									collapsed={collapsed}
									onClick={() => handleItemClick(item.id)}
								/>
							))}
						</List>
					</Box>
				))}
			</Box>

			{/* Footer */}
			{filteredBottomItems.length > 0 && (
				<Box
					sx={{
						px: collapsed ? 0.75 : 1.25,
						pt: 1,
						pb: 1.5,
						borderTop: "1px solid rgba(15, 23, 42, 0.06)",
					}}
				>
					<List sx={{ py: 0 }}>
						{filteredBottomItems.map((item) => (
							<NavItem
								key={item.id}
								item={item}
								active={activeSection === item.id}
								collapsed={collapsed}
								onClick={() => handleItemClick(item.id)}
							/>
						))}
					</List>
				</Box>
			)}

			{/* Collapse toggle */}
			{!isMobile && (
				<Box
					onClick={onToggleCollapse}
					sx={{
						position: "absolute",
						right: -11,
						top: 86,
						width: 22,
						height: 22,
						borderRadius: "50%",
						backgroundColor: "#fff",
						border: "1px solid rgba(15, 23, 42, 0.1)",
						display: "flex",
						alignItems: "center",
						justifyContent: "center",
						cursor: "pointer",
						boxShadow: "0 2px 8px rgba(15, 23, 42, 0.08)",
						transition: "all 0.15s ease",
						zIndex: 10,
						"&:hover": {
							backgroundColor: ACCENT,
							borderColor: ACCENT,
							"& svg": { color: "#fff" },
						},
					}}
				>
					{collapsed ? (
						<KeyboardArrowRightIcon sx={{ fontSize: 15, color: "#64748B" }} />
					) : (
						<KeyboardArrowLeftIcon sx={{ fontSize: 15, color: "#64748B" }} />
					)}
				</Box>
			)}
		</Box>
	);

	if (isMobile) {
		return (
			<>
				{mobileOpen && (
					<Box
						onClick={onMobileClose}
						sx={{
							position: "fixed",
							inset: 0,
							backgroundColor: "rgba(15, 23, 42, 0.4)",
							backdropFilter: "blur(4px)",
							zIndex: 1199,
						}}
					/>
				)}
				<Box
					sx={{
						position: "fixed",
						left: mobileOpen ? 0 : -SIDEBAR_WIDTH,
						top: 0,
						bottom: 0,
						zIndex: 1200,
						transition: "left 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
					}}
				>
					{sidebarContent}
				</Box>
			</>
		);
	}

	return (
		<Box
			sx={{
				position: "fixed",
				left: 0,
				top: 0,
				bottom: 0,
				zIndex: 1100,
			}}
		>
			{sidebarContent}
		</Box>
	);
};

export { SIDEBAR_WIDTH, SIDEBAR_COLLAPSED_WIDTH };
export default Sidebar;
